import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Save, Close, Warning } from '@mui/icons-material';
import { useCompanyStore } from '@/stores/company-store';
import { useSnackbar } from '@certara/common-react-components';
import {
  useCanAccessConfiguration,
  useConfiguration,
  useSaveConfiguration,
  useSupportActions,
  useLoadAllApplications,
  useUpdateAllApplications,
  useDeleteAllApplications,
  useInitializeStaticData,
} from '@/hooks/use-configuration';
import { useUIStore } from '@/stores/ui-store';

export function ConfigurationDialog() {
  const { activeCompany } = useCompanyStore();
  const { showSuccess, showError } = useSnackbar();
  const open = useUIStore((state) => state.configurationDialogOpen);
  const setOpen = useUIStore((state) => state.setConfigurationDialogOpen);

  const { data: config, isLoading, error } = useConfiguration(activeCompany?.Id);
  const {
    data: canAccessConfiguration,
    isLoading: isPermLoading,
    error: permError,
  } = useCanAccessConfiguration(activeCompany?.Id);
  const saveConfiguration = useSaveConfiguration(activeCompany?.Id ?? 0);

  const { data: supportPermissions } = useSupportActions(activeCompany?.Id);
  const loadAll = useLoadAllApplications(activeCompany?.Id ?? 0);
  const updateAll = useUpdateAllApplications(activeCompany?.Id ?? 0);
  const deleteAll = useDeleteAllApplications(activeCompany?.Id ?? 0);
  const initStatic = useInitializeStaticData(activeCompany?.Id ?? 0);

  const [reviewPath, setReviewPath] = useState('');
  const [publishPath, setPublishPath] = useState('');
  const [allowReload, setAllowReload] = useState(false);
  const [processPdf, setProcessPdf] = useState(false);
  const [verifyChecksums, setVerifyChecksums] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [confirmAction, setConfirmAction] = useState<{
    title: string;
    description: string;
    onConfirm: () => Promise<void>;
  } | null>(null);
  const [isActionRunning, setIsActionRunning] = useState(false);

  const executeSupportAction = useCallback(
    async (action: () => Promise<unknown>, label: string) => {
      setIsActionRunning(true);
      try {
        await action();
        showSuccess(`${label} queued successfully`);
      } catch (error) {
        showError(`${label} failed`, (error as Error).message);
      } finally {
        setIsActionRunning(false);
        setConfirmAction(null);
      }
    },
    [showSuccess, showError]
  );

  // Load configuration data when dialog opens
  useEffect(() => {
    if (config) {
      setReviewPath(config.ReviewFolder || '');
      setPublishPath(config.PublishFolder || '');
      setAllowReload(config.AllowReload || false);
      setProcessPdf(config.ValidatePdfs || false);
      setVerifyChecksums(config.ValidateChecksums || false);
      setHasChanges(false);
    }
  }, [config]);

  const handleClose = () => {
    if (hasChanges) {
      if (confirm('You have unsaved changes. Are you sure you want to close?')) {
        setOpen(false);
        setHasChanges(false);
      }
    } else {
      setOpen(false);
    }
  };

  const handleSave = async () => {
    try {
      await saveConfiguration.mutateAsync({
        ReviewFolder: reviewPath,
        PublishFolder: publishPath,
        AllowReload: allowReload,
        ValidatePdfs: processPdf,
        ValidateChecksums: verifyChecksums,
      });
      showSuccess('Configuration saved successfully!');
      setHasChanges(false);
      setOpen(false);
    } catch (error) {
      showError('Failed to save configuration.', (error as Error).message);
    }
  };

  const markChanged = () => {
    if (!hasChanges) setHasChanges(true);
  };

  if (!activeCompany) {
    return (
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        data-testid="configuration-dialog-no-company"
      >
        <DialogTitle data-testid="configuration-dialog-title">Configuration</DialogTitle>
        <DialogContent>
          <Alert severity="warning" data-testid="configuration-dialog-no-company-alert">
            Please select a space to configure settings.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            data-testid="configuration-dialog-close-button"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (canAccessConfiguration === false) {
    return (
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        data-testid="configuration-dialog-no-access"
      >
        <DialogTitle data-testid="configuration-dialog-title">Configuration</DialogTitle>
        <DialogContent>
          <Alert severity="error" data-testid="configuration-dialog-no-access-alert">
            You do not have permission to access configuration for this space.
          </Alert>
          {permError && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              data-testid="configuration-dialog-permission-error"
            >
              {(permError as Error).message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            data-testid="configuration-dialog-close-button"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      data-testid="configuration-dialog"
    >
      <DialogTitle data-testid="configuration-dialog-title">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Configuration</Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid="configuration-dialog-company-name"
          >
            {activeCompany.Name}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers data-testid="configuration-dialog-content">
        {(isLoading || isPermLoading) && (
          <Box
            display="flex"
            justifyContent="center"
            p={4}
            data-testid="configuration-dialog-loading"
          >
            <CircularProgress data-testid="configuration-dialog-loading-spinner" />
          </Box>
        )}

        {(error || permError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load configuration:{' '}
            {(error as Error)?.message || (permError as Error)?.message}
          </Alert>
        )}

        {!isLoading && !error && (canAccessConfiguration ?? true) && (
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Folder Paths Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Folder Paths
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  data-testid="configuration-dialog-review-path-input"
                  fullWidth
                  label="Review Folder Path"
                  value={reviewPath}
                  onChange={(e) => {
                    setReviewPath(e.target.value);
                    markChanged();
                  }}
                  placeholder="C:\Path\To\Review\Folder"
                  helperText="Path to the folder where files will be reviewed"
                />
                <TextField
                  data-testid="configuration-dialog-publish-path-input"
                  fullWidth
                  label="Publish Folder Path"
                  value={publishPath}
                  onChange={(e) => {
                    setPublishPath(e.target.value);
                    markChanged();
                  }}
                  placeholder="C:\Path\To\Publish\Folder"
                  helperText="Path to the folder where files will be published"
                />
              </Box>
            </Box>

            <Divider />

            {/* Processing Options Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Processing Options
              </Typography>
              <Box display="flex" flexDirection="column">
                <FormControlLabel
                  control={
                    <Checkbox
                      data-testid="configuration-dialog-allow-reload-checkbox"
                      checked={allowReload}
                      onChange={(e) => {
                        setAllowReload(e.target.checked);
                        markChanged();
                      }}
                    />
                  }
                  label="Allow Reload"
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 4, mt: -1, mb: 1 }}
                >
                  Allow reloading of previously processed files
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      data-testid="configuration-dialog-process-pdf-checkbox"
                      checked={processPdf}
                      onChange={(e) => {
                        setProcessPdf(e.target.checked);
                        markChanged();
                      }}
                    />
                  }
                  label="Process PDF Files"
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 4, mt: -1, mb: 1 }}
                >
                  Automatically process PDF files during load operations
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      data-testid="configuration-dialog-verify-checksums-checkbox"
                      checked={verifyChecksums}
                      onChange={(e) => {
                        setVerifyChecksums(e.target.checked);
                        markChanged();
                      }}
                    />
                  }
                  label="Verify Checksums"
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 4, mt: -1 }}
                >
                  Verify MD5 checksums during file processing
                </Typography>
              </Box>
            </Box>

            {/* Support Actions Section - only visible to SUPPORT users */}
            {supportPermissions?.isSupport && (
              <>
                <Divider />

                <Box>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Support Actions
                  </Typography>
                  <Alert
                    severity="warning"
                    icon={<Warning />}
                    sx={{ mb: 2 }}
                    data-testid="configuration-dialog-support-warning"
                  >
                    The following are special actions which should only be used with the
                    approval of Tier 3 support.
                  </Alert>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Button
                      variant="outlined"
                      color="warning"
                      disabled={isActionRunning}
                      data-testid="configuration-dialog-load-all-button"
                      onClick={() =>
                        setConfirmAction({
                          title: 'Load All Applications',
                          description:
                            'This will queue a load operation for all applications in this space. Are you sure you want to proceed?',
                          onConfirm: () =>
                            executeSupportAction(
                              () => loadAll.mutateAsync(),
                              'Load All Applications'
                            ),
                        })
                      }
                    >
                      Load All Applications
                    </Button>

                    {supportPermissions.isCompanyOne && (
                      <Button
                        variant="outlined"
                        color="warning"
                        disabled={isActionRunning}
                        data-testid="configuration-dialog-update-all-button"
                        onClick={() =>
                          setConfirmAction({
                            title: 'Update All Applications',
                            description:
                              'This will queue an update operation for all applications. Are you sure you want to proceed?',
                            onConfirm: () =>
                              executeSupportAction(
                                () => updateAll.mutateAsync(),
                                'Update All Applications'
                              ),
                          })
                        }
                      >
                        Update All Applications
                      </Button>
                    )}

                    {supportPermissions.deleteAllEnabled && (
                      <Button
                        variant="outlined"
                        color="error"
                        disabled={isActionRunning}
                        data-testid="configuration-dialog-delete-all-button"
                        onClick={() =>
                          setConfirmAction({
                            title: 'Delete All Applications',
                            description:
                              'This will permanently delete all applications in this space. This action cannot be undone. Are you sure you want to proceed?',
                            onConfirm: () =>
                              executeSupportAction(
                                () => deleteAll.mutateAsync(),
                                'Delete All Applications'
                              ),
                          })
                        }
                      >
                        Delete All Applications
                      </Button>
                    )}

                    {supportPermissions.isCompanyOne && (
                      <Button
                        variant="outlined"
                        color="warning"
                        disabled={isActionRunning}
                        data-testid="configuration-dialog-init-static-button"
                        onClick={() =>
                          setConfirmAction({
                            title: 'Initialize Static Data',
                            description:
                              'This will re-initialize all static data. Are you sure you want to proceed?',
                            onConfirm: () =>
                              executeSupportAction(
                                () => initStatic.mutateAsync(),
                                'Initialize Static Data'
                              ),
                          })
                        }
                      >
                        Initialize Static Data
                      </Button>
                    )}
                  </Box>
                </Box>
              </>
            )}

            {hasChanges && <Alert severity="info">You have unsaved changes</Alert>}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          startIcon={<Close />}
          data-testid="configuration-dialog-cancel-button"
        >
          Cancel
        </Button>
        <Button
          data-testid="configuration-dialog-save-button"
          onClick={handleSave}
          variant="contained"
          startIcon={<Save />}
          disabled={!hasChanges || isLoading}
        >
          Save Changes
        </Button>
      </DialogActions>

      {/* Support Action Confirmation Dialog */}
      <Dialog
        open={!!confirmAction}
        onClose={() => !isActionRunning && setConfirmAction(null)}
        maxWidth="sm"
        fullWidth
        data-testid="support-action-confirm-dialog"
      >
        <DialogTitle data-testid="support-action-confirm-title">
          {confirmAction?.title}
        </DialogTitle>
        <DialogContent>
          <Typography data-testid="support-action-confirm-description">
            {confirmAction?.description}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmAction(null)}
            disabled={isActionRunning}
            data-testid="support-action-confirm-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmAction?.onConfirm}
            variant="contained"
            color="warning"
            disabled={isActionRunning}
            startIcon={isActionRunning ? <CircularProgress size={16} /> : undefined}
            data-testid="support-action-confirm-proceed"
          >
            {isActionRunning ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
